/**
 * Crypto utility functions for API request signing
 */

import axios from 'axios';
import { getRandomValues, getSubtle } from '../platform/crypto';

/**
 * Generate a random nonce string
 * @returns {string} A random nonce
 */
export const generateNonce = () => {
  const array = new Uint8Array(16);
  getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Get current Unix timestamp in seconds
 * @returns {number} Unix timestamp
 */
export const getUnixTimestamp = () => {
  return Math.floor(Date.now() / 1000);
};

/**
 * Generate HMAC-SHA256 signature
 * @param {string} secretKey - The shared secret key
 * @param {string} message - The message to sign
 * @returns {Promise<string>} The HMAC-SHA256 signature as hex string
 */
export const generateHmacSha256 = async (secretKey, message) => {
  // Convert the secret key to ArrayBuffer
  const encoder = new TextEncoder();
  const keyData = encoder.encode(secretKey);

  // Import the key for HMAC
  const key = await getSubtle().importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  // Sign the message
  const messageData = encoder.encode(message);
  const signature = await getSubtle().sign('HMAC', key, messageData);

  // Convert to hex string
  const hashArray = Array.from(new Uint8Array(signature));
  const hashHex = hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');

  return hashHex;
};

/**
 * PATH part of the signed string: pathname from the first /v{n} segment
 * (mirrors canonical_signed_path in the backend's security/service.py)
 * @param {string} pathname - URL pathname (no query string)
 * @returns {string} The canonical signed path
 */
export const canonicalSignedPath = (pathname) => {
  const match = /\/v\d+(?=\/|$)/.exec(pathname);
  return match ? pathname.slice(match.index) : pathname;
};

/**
 * Create the signature payload for a request.
 *
 * Must match build_signed_string in the backend's security/service.py byte
 * for byte: METHOD|PATH|TIMESTAMP|NONCE|BODY
 *
 *   METHOD     HTTP method, upper-case
 *   PATH       decodeURIComponent(URL pathname) from the first /v{n} segment,
 *              no query string (/api/v1/x and /v1/x both sign /v1/x)
 *   TIMESTAMP  Unix seconds (X-Timestamp)
 *   NONCE      X-Nonce value
 *   BODY       the request body as sent, empty string when there is none
 *
 * The secret ships in the bundle: this deters casual abuse, it is not a
 * security boundary.
 *
 * @param {object} config - Axios request config
 * @param {string} nonce - The nonce value
 * @param {number} timestamp - Unix timestamp
 * @returns {string} The message to be signed
 */
export const createSignaturePayload = (config, nonce, timestamp) => {
  const method = (config.method || 'GET').toUpperCase();

  // the URL axios will request (baseURL + url, relative bases resolved against
  // the page); query strings are not signed, the path is signed decoded
  const rawPathname = new URL(axios.getUri(config), window.location.origin).pathname;
  let pathname;
  try {
    pathname = decodeURIComponent(rawPathname);
  } catch (error) {
    if (!(error instanceof URIError)) throw error;
    pathname = rawPathname;
  }

  // axios JSON-serialises object bodies with JSON.stringify, so this matches the wire body
  let body = '';
  if (config.data !== undefined && config.data !== null) {
    body = typeof config.data === 'string' ? config.data : JSON.stringify(config.data);
  }

  return `${method}|${canonicalSignedPath(pathname)}|${timestamp}|${nonce}|${body}`;
};
